import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Col, Row, Spinner } from 'reactstrap';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import useApi from 'hooks/useApi';
import { removeBreadcrumb } from 'redux/actions/layoutActions';

import {
  loadAllCustomFields,
  removeCustomField,
  updateCustomFieldList,
} from 'redux/actions/customFieldActions';

import { ReactComponent as SpecialAreaIcon } from 'assets/icon-special-area.svg';
import { ReactComponent as ArrowDownIcon } from 'assets/icon-arrow-down.svg';
import { ReactComponent as ArrowUpIcon } from 'assets/icon-arrow-up.svg';
import { ReactComponent as TrashIcon } from 'assets/icon-trash.svg';
import { ReactComponent as ConfigurationIcon } from 'assets/icon-configuration.svg';
import BackgroundBox from 'shared/components/BackgroundBox';
import { Button, ButtonIcon } from 'shared/components/Buttons';
import { TextField, Switch } from 'shared/components/form/Inputs';
import DropdownSearch from 'shared/components/DropdownSearch';
import MultipleSelectSearch from 'shared/components/MultipleSelectSearch';
import TextArea from 'shared/components/textarea/TextArea';
import InputDate from 'shared/components/inputdate/InputDate';
import ConfirmModal from 'shared/components/ConfirmModal';
import AlertModal from 'shared/components/AlertModal';

import FieldType from 'shared/helpers/fieldType';
import { emptyString } from 'shared/utils';

import GroupFieldComponent from './GroupFieldComponent';
import { CustomFieldFormWrapper, CustomFieldText, CustomFieldLabel, EmptyForm } from './styles';

const CustomFieldForm = ({ onUpdateField }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const { customAreaApi } = useApi();
  const customField = useSelector((state) => state.customField);
  const { breadcrumbs } = useSelector((state) => state.layout);
  const theme = useSelector((state) => state.theme);
  const { cusNavId } = useSelector((state) => state.auth);
  const { selectedDefaultLanguage } = useSelector((state) => state.topBar);
  const [sortCustomFields, setSortCustomFields] = useState([]);
  const [selectedRemoveField, setSelectedRemoveField] = useState();
  const [showConfirmRemoveField, setShowConfirmRemoveField] = useState();
  const [showSuccessRemove, setShowSuccessRemove] = useState();
  const [showErrorRemove, setShowErrorRemove] = useState(false);
  const [messageError, setMessageError] = useState('');
  const [showError, setShowError] = useState(false);
  const [{ loading }, getCustomFieldData] = customAreaApi(
    {
      url: `${window.API_V1}/cus/fields/${cusNavId}`,
      method: 'GET',
    },
    { manual: true }
  );
  const [{ loading: loadingCreate }, createCustomField] = customAreaApi(
    {
      url: `${window.API_V1}/cus/fields/${cusNavId}`,
      method: 'POST',
    },
    { manual: true }
  );
  const [{ loading: loadingUpdate }, updateCustomFields] = customAreaApi(
    {
      url: `${window.API_V1}/cus/fields/${cusNavId}/modify`,
      method: 'POST',
    },
    { manual: true }
  );
  const [{ loading: lodingDelete }, deleteCustomField] = customAreaApi(
    {
      method: 'DELETE',
    },
    { manual: true }
  );
  const { mainColor } = theme;

  const getNameSelectedLanguage = (i18List) => {
    if (i18List) {
      const findName = i18List.find((x) => x.i18Code === selectedDefaultLanguage);
      if (findName && findName?.i18Name !== '') {
        return findName?.i18Name;
      }
      return i18List[0].i18Name;
    }
    return null;
  };

  const selectedMultipleList = (members) => {
    let newMembersMultiple = [];
    members
      .filter((item) => item.type !== FieldType.GROUP && item.id)
      .forEach((member) => {
        newMembersMultiple = [
          ...newMembersMultiple,
          { name: getNameSelectedLanguage(member.i18List), id: member.id },
        ];
      });
    return newMembersMultiple;
  };

  useEffect(() => {
    const bootAsync = async () => {
      try {
        const {
          data: { errors, data },
        } = await getCustomFieldData();
        if (errors.length) {
          return;
        }
        dispatch(loadAllCustomFields(data.filter((x) => x.id)));
      } catch (error) {
        if (error.response?.status === 400) {
          dispatch(loadAllCustomFields([]));
        }
        console.error('Error getting translated custom fields: ', error);
      }
    };
    if (!loading) {
      bootAsync();
    }
  }, []);

  useEffect(() => {
    const customFieldsWithoutGroup = customField.customFields.filter(
      (currentCustomField) => emptyString(currentCustomField.parentId) && currentCustomField.id
    );
    setSortCustomFields([...customFieldsWithoutGroup].sort((a, b) => a.order - b.order));
  }, [customField.customFields]);

  const onRemove = (currentField) => {
    setSelectedRemoveField(currentField);
    setShowConfirmRemoveField(true);
  };

  const onClickUpdate = (currentField) => {
    onUpdateField(currentField);
  };

  const getFieldComponent = (field) => {
    const fields = new Map([
      [
        FieldType.GROUP,
        <GroupFieldComponent
          groupField={field}
          onRemove={onRemove}
          onClickUpdate={onClickUpdate}
        />,
      ],
      [FieldType.TEXT, <TextField />],
      [FieldType.TEXT_AREA, <TextArea onChange={() => null} />],
      [FieldType.NUMBER, <TextField type="number" />],
      [FieldType.CURRENCY, <TextField type="number" />],
      [
        FieldType.LIST,
        <DropdownSearch
          id={`custom-field-dropdown-search-${field.id}`}
          options={
            field.members
              ? [...field.members]
                  .sort((memberA, memberB) => memberA.order - memberB.order)
                  .map((member) => ({
                    name: getNameSelectedLanguage(member.i18List),
                    value: member.id,
                  }))
              : []
          }
        />,
      ],
      [
        FieldType.MULTI_LIST,
        <MultipleSelectSearch
          dataSource={selectedMultipleList(
            field.members
              ? [...field.members].sort((memberA, memberB) => memberA.order - memberB.order)
              : []
          )}
          fields={{ text: 'name', value: 'id' }}
        />,
      ],
      [FieldType.OPTION, <Switch id={field.id} color={mainColor} onChange={() => null} />],
      [FieldType.CALENDAR, <InputDate className="input-date" />],
    ]);

    return field.type ? fields.get(field.type) ?? null : null;
  };

  const onToggleErrorRemove = () => setShowErrorRemove((currentShow) => !currentShow);

  const onToggleError = () => setShowError((currentShow) => !currentShow);

  const onToggleSuccessRemove = () => setShowSuccessRemove((currentShow) => !currentShow);

  const onOkSuccessRemove = () => {
    setSelectedRemoveField(undefined);
    onToggleSuccessRemove();
  };

  const removeSuccess = () => {
    try {
      if (selectedRemoveField !== undefined && selectedRemoveField !== null) {
        dispatch(removeCustomField(selectedRemoveField.id));
        setShowConfirmRemoveField(false);
        onToggleSuccessRemove();
      }
    } catch (ex) {
      throw new Error(ex);
    }
  };

  const onCancelRemove = () => {
    setShowConfirmRemoveField(false);
    setSelectedRemoveField(undefined);
  };

  const onOkRemove = async () => {
    try {
      if (lodingDelete) {
        return;
      }
      if (selectedRemoveField.isNew) {
        removeSuccess();
        return;
      }
      const {
        data: { errors },
      } = await deleteCustomField({
        url: `${window.API_V1}/cus/fields/${cusNavId}/${selectedRemoveField.id}`,
      });
      if (errors.length) {
        throw new Error(
          errors.reduce((actual, current) => `${actual}\n${current.message}`),
          ''
        );
      }
      removeSuccess();
    } catch (ex) {
      if (ex.response?.data?.errors[0].code === 'CAFI-06') {
        setMessageError(t('custom_field_delete_error_message_with_data'));
      } else {
        setMessageError(t('custom_field_delete_error_message'));
      }
      setShowConfirmRemoveField(false);
      onToggleErrorRemove();
    }
  };

  const onSort = (item, fromIndex, toIndex, animateClass) => {
    if (toIndex < 0) {
      return;
    }

    if (toIndex > sortCustomFields.length) {
      return;
    }
    const modifieCustomFields = sortCustomFields.slice();
    modifieCustomFields.splice(toIndex, 0, modifieCustomFields.splice(fromIndex, 1)[0]);
    const newSortCustomFields = modifieCustomFields.map((currentField) =>
      currentField.id === item.id ? { ...currentField, animateClass } : currentField
    );
    setSortCustomFields(newSortCustomFields);
    // Clear animation
    setTimeout(() => {
      // Update reducer custom field
      const customFieldsToUpdate = newSortCustomFields.map(
        ({ animateClass: animateClassField, ...rest }, index) => ({
          ...rest,
          order: index,
          updated: true,
        })
      );
      dispatch(updateCustomFieldList(customFieldsToUpdate));
    }, 800);
  };

  const onAdd = async () => {
    try {
      // Add new custom fields
      const newCustomFields = customField.customFields
        .filter((field) => field.isNew)
        .map(({ isNew, ...rest }) => rest);
      if (newCustomFields.length) {
        const {
          data: { errors },
        } = await createCustomField({ data: newCustomFields });
        if (errors.length) {
          throw new Error(
            errors.reduce((actual, current) => `${actual}\n${current.message}`),
            ''
          );
        }
      }
      // Update current custom fields
      const updatedCustomFields = customField.customFields
        .filter((field) => !field.isNew && field.updated)
        .map(({ updated, ...rest }) => rest);
      if (updatedCustomFields.length) {
        const {
          data: { errors: errorsFromUpdate },
        } = await updateCustomFields({ data: updatedCustomFields });
        if (errorsFromUpdate.length) {
          throw new Error(
            errorsFromUpdate.reduce((actual, current) => `${actual}\n${current.message}`),
            ''
          );
        }
      }
      // Refreshing custom field list
      const {
        data: { errors: errorsFromGet, data },
      } = await getCustomFieldData();
      if (errorsFromGet.length) {
        throw new Error(
          errorsFromGet.reduce((actual, current) => `${actual}\n${current.message}`),
          ''
        );
      }
      dispatch(loadAllCustomFields(data));
    } catch (ex) {
      console.error(ex);
      setShowError(true);
    }
  };

  const onCancel = async () => {
    history.goBack();
    if (breadcrumbs.length > 1) {
      breadcrumbs.forEach(() => {
        dispatch(removeBreadcrumb());
      });
    }
    try {
      const {
        data: { errors, data },
      } = await getCustomFieldData();
      if (errors.length) {
        throw new Error(
          errors.reduce((actual, current) => `${actual}\n${current.message}`),
          ''
        );
      }
      dispatch(loadAllCustomFields(data));
    } catch (ex) {
      setShowError(true);
    }
  };
  return (
    <CustomFieldFormWrapper>
      <form className="form">
        <Row className="row-box">
          <Col sm={12}>
            <BackgroundBox className="backgroundbox">
              {(loading || loadingCreate || loadingUpdate) && (
                <div className="d-flex justify-content-center mb-5 align-items-center w-100 h-100">
                  <Spinner color={mainColor} />
                </div>
              )}
              {sortCustomFields.length <= 0 && !loading && (
                <EmptyForm>
                  <SpecialAreaIcon />
                  <CustomFieldText>{t('custom_field_not_found_custom_fields')}</CustomFieldText>
                  <CustomFieldText className="subText">
                    {t('custom_field_not_found_subtext_custom_fields')}
                  </CustomFieldText>
                </EmptyForm>
              )}
              {sortCustomFields.length >= 1 && !loading && !loadingCreate && !loadingUpdate && (
                <div className="form-fields-container">
                  {sortCustomFields
                    .filter((x) => x.id)
                    .map((currentCustomField, index) => (
                      <React.Fragment key={currentCustomField.id}>
                        <Row className={classNames(['row-field', currentCustomField.animateClass])}>
                          <Col
                            className={classNames({
                              'custom-col-field': true,
                              'custom-col-field-not-group':
                                currentCustomField.type !== FieldType.GROUP,
                            })}
                            sm={11}
                          >
                            <div className="form__form-group">
                              <CustomFieldLabel
                                className={classNames({
                                  'text-uppercase': currentCustomField.type === FieldType.GROUP,
                                })}
                              >
                                {getNameSelectedLanguage(currentCustomField.i18List)}
                              </CustomFieldLabel>
                              {getFieldComponent(currentCustomField)}
                            </div>
                          </Col>
                          <Col
                            className={classNames([
                              'd-flex flex-row justify-content-custom-field remove-padding-horizontal',
                              {
                                'align-items-center': currentCustomField.type !== FieldType.GROUP,
                                'pt-4': currentCustomField.type === FieldType.GROUP,
                              },
                            ])}
                            sm={1}
                          >
                            <ButtonIcon
                              className="action-icon"
                              Icon={() => <ArrowDownIcon />}
                              onClick={() =>
                                onSort(currentCustomField, index, index + 1, 'animate-down')
                              }
                            />
                            <ButtonIcon
                              className="action-icon"
                              Icon={() => <ArrowUpIcon />}
                              onClick={() =>
                                onSort(currentCustomField, index, index - 1, 'animate-up')
                              }
                            />
                            <ButtonIcon
                              className="action-icon"
                              Icon={() => <TrashIcon />}
                              onClick={() => onRemove(currentCustomField)}
                            />
                            <ButtonIcon
                              className="action-icon"
                              Icon={() => <ConfigurationIcon />}
                              onClick={() => onClickUpdate(currentCustomField)}
                            />
                          </Col>
                        </Row>
                      </React.Fragment>
                    ))}
                </div>
              )}
            </BackgroundBox>
          </Col>
        </Row>
        <Row className="row-actions">
          <Col className="d-flex flex-row justify-content-end" sm={12}>
            <Button
              className="text-uppercase mr-4"
              type="button"
              color="secondary"
              label={t('general_button_cancel')}
              onClick={onCancel}
            />
            <Button
              className="text-uppercase"
              type="button"
              color="primary"
              label={t('general_button_save')}
              onClick={onAdd}
            />
          </Col>
        </Row>
      </form>
      <ConfirmModal
        isOpen={showConfirmRemoveField}
        toggle={onCancelRemove}
        confirmMessage={t('custom_feild_delete_field_confirmation').replace(
          '{fieldName}',
          getNameSelectedLanguage(selectedRemoveField?.i18List)
        )}
        cancelText={t('general_button_no')}
        okText={t('general_button_yes')}
        onCancel={onCancelRemove}
        onOk={onOkRemove}
      />
      <AlertModal
        type="success"
        isOpen={showSuccessRemove}
        toggle={onOkSuccessRemove}
        message={t('custom_feild_delete_field_success').replace(
          '{fieldName}',
          getNameSelectedLanguage(selectedRemoveField?.i18List)
        )}
        okText={t('general_button_ok')}
        onOk={onOkSuccessRemove}
      />
      <AlertModal
        type="error"
        isOpen={showErrorRemove}
        toggle={onToggleErrorRemove}
        message={messageError}
        okText={t('general_button_ok')}
        onOk={onToggleErrorRemove}
      />
      <AlertModal
        type="error"
        isOpen={showError}
        toggle={onToggleError}
        message={t('custom_field_operation_error_message')}
        okText={t('general_button_ok')}
        onOk={onToggleError}
      />
    </CustomFieldFormWrapper>
  );
};

CustomFieldForm.propTypes = {
  onUpdateField: PropTypes.func.isRequired,
};

export default CustomFieldForm;
